import { ApolloClient, ApolloQueryResult } from 'apollo-client';
import { ApolloLink, DocumentNode, FetchResult } from 'apollo-link';
import { createHttpLink } from 'apollo-link-http';
import { RetryLink } from 'apollo-link-retry';
import { InMemoryCache, NormalizedCacheObject } from 'apollo-cache-inmemory';
import { prouniAuthLink } from '@/helpers/westeros';

/**
 * Dedicated Apollo client for the ProUni flow. It points to the same
 * ug-enrollments `/graphql` endpoint used by the ARCANEUM microservice, but
 * authenticates with the pocket session cookie `d` (Bearer) instead of the
 * `admissions-auth-token`. Kept isolated from `westeros.ts`'s default client
 * so the enrollment flow is untouched.
 */
let prouniApolloClient: ApolloClient<NormalizedCacheObject> | undefined;

export function getProuniApolloClient(): ApolloClient<NormalizedCacheObject> {
  if (!prouniApolloClient) {
    const uri = process.env.VUE_APP_ARCANEUM_GRAPHQL;

    const link = ApolloLink.from([new RetryLink(), prouniAuthLink, createHttpLink({ uri })]);

    prouniApolloClient = new ApolloClient({
      link,
      cache: new InMemoryCache(),
      connectToDevTools: process.env.NODE_ENV === 'development',
      defaultOptions: { query: { fetchPolicy: 'no-cache' } },
    });
  }

  return prouniApolloClient;
}

export async function prouniQuery<T>(
  query: DocumentNode,
  variables?: Record<string, any>,
): Promise<ApolloQueryResult<T>> {
  return getProuniApolloClient().query<T>({ query, variables });
}

export async function prouniMutation<T>(
  mutation: DocumentNode,
  variables?: Record<string, any>,
): Promise<FetchResult<T>> {
  return getProuniApolloClient().mutate<T>({ mutation, variables });
}
